import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Coins, Loader2 } from "lucide-react";

const fmtUsd = (c) => `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

/** Slice 2 — "Convert Commission to Coins" (deliberately NOT "earnings":
 * only cleared Ambassador commissions are convertible, never Stripe tips). */
export default function ConvertCommissionCard() {
  const [info, setInfo] = useState(null);
  const [history, setHistory] = useState([]);
  const [dollars, setDollars] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: c }, { data: h }] = await Promise.all([
        api.get("/earnings/convertible"),
        api.get("/earnings/conversions", { params: { limit: 5 } }),
      ]);
      setInfo(c);
      setHistory(h.conversions || []);
    } catch { /* card hides */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!info || (info.total_cents === 0 && history.length === 0)) return null;

  const cents = Math.round((parseFloat(dollars) || 0)) * 100;
  const coins = (cents * info.rate_coins_per_usd) / 100;
  const maxNow = Math.min(info.cleared_cents, info.remaining_today_cents);
  const valid = cents >= info.min_cents && cents <= maxNow;

  const convert = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/earnings/convert", {
        cents,
        idempotency_key: `card:${Date.now()}`,
      });
      toast.success(`Converted ${fmtUsd(data.cents)} → ${data.coins.toLocaleString()} coins 🐼`);
      setDollars("");
      setConfirming(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Conversion failed — nothing was changed.");
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card-surface p-5" data-testid="convert-commission-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Ambassador Commission</div>
          <h3 className="font-display text-xl">Convert Commission to Coins</h3>
        </div>
        <Coins size={18} className="text-amber-400" />
      </div>
      <div className="grid grid-cols-3 gap-2 mb-4 text-center">
        <div className="rounded-xl bg-white/[0.04] border border-white/10 py-2">
          <div className="text-sm font-semibold text-emerald-400" data-testid="convert-cleared">{fmtUsd(info.cleared_cents)}</div>
          <div className="text-[9px] uppercase tracking-widest text-zinc-500">Cleared</div>
        </div>
        <div className="rounded-xl bg-white/[0.04] border border-white/10 py-2">
          <div className="text-sm font-semibold text-zinc-400" data-testid="convert-pending">{fmtUsd(info.pending_cents)}</div>
          <div className="text-[9px] uppercase tracking-widest text-zinc-500">Clearing ({info.clearing_days}d)</div>
        </div>
        <div className="rounded-xl bg-white/[0.04] border border-white/10 py-2">
          <div className="text-sm font-semibold text-white">{fmtUsd(info.remaining_today_cents)}</div>
          <div className="text-[9px] uppercase tracking-widest text-zinc-500">Left today</div>
        </div>
      </div>
      {info.cleared_cents >= info.min_cents && (
        <>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">$</span>
              <input
                data-testid="convert-amount-input"
                type="number"
                min={info.min_cents / 100}
                step="1"
                value={dollars}
                onChange={(e) => { setDollars(e.target.value); setConfirming(false); }}
                placeholder={`${info.min_cents / 100} min · whole dollars`}
                className="w-full bg-black/40 border border-white/15 rounded-full pl-7 pr-3 py-2.5 text-sm text-white focus:border-amber-400 outline-none"
              />
            </div>
            <ArrowRight size={14} className="text-zinc-600 shrink-0" />
            <div className="text-sm font-semibold text-amber-300 whitespace-nowrap" data-testid="convert-coin-preview">
              {cents > 0 ? `${coins.toLocaleString()} 🐼` : "— 🐼"}
            </div>
          </div>
          {!confirming ? (
            <Button
              onClick={() => setConfirming(true)}
              disabled={!valid}
              data-testid="convert-review-button"
              className="mt-3 w-full rounded-full bg-white/10 border border-white/15 text-white hover:border-amber-400 disabled:opacity-40"
            >
              Review conversion
            </Button>
          ) : (
            <div className="mt-3 rounded-xl border border-amber-400/40 bg-amber-950/20 p-3" data-testid="convert-confirm-box">
              <p className="text-xs text-amber-200/90 mb-2">
                Convert <b>{fmtUsd(cents)}</b> of cleared commission into <b>{coins.toLocaleString()} coins</b>?
                This is <b>final</b> — coins can't be converted back to cash.
              </p>
              <div className="flex gap-2">
                <Button onClick={convert} disabled={busy} data-testid="convert-confirm-button"
                  className="flex-1 rounded-full btn-crimson">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : "Convert — it's final"}
                </Button>
                <Button onClick={() => setConfirming(false)} disabled={busy}
                  className="rounded-full bg-white/10 border border-white/15 text-white">Cancel</Button>
              </div>
            </div>
          )}
        </>
      )}
      {info.cleared_cents < info.min_cents && (
        <p className="text-xs text-zinc-500">
          Minimum conversion is {fmtUsd(info.min_cents)}. Commissions clear {info.clearing_days} days after they're earned.
        </p>
      )}
      {history.length > 0 && (
        <div className="mt-4 space-y-1" data-testid="convert-history">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Recent conversions</div>
          {history.map((c) => (
            <div key={c.id} className="flex items-center justify-between text-xs py-1 border-b border-white/5 last:border-0">
              <span className="text-zinc-400">{new Date(c.created_at).toLocaleDateString()}</span>
              <span className="text-white">{fmtUsd(c.cents)} → {c.coins.toLocaleString()} 🐼</span>
              <span className={c.status === "completed" ? "text-emerald-400" : "text-rose-400"}>{c.status}</span>
            </div>
          ))}
        </div>
      )}
      <p className="mt-3 text-[10px] text-zinc-600">
        Only cleared Ambassador commissions can be converted. Stripe tips pay out to your bank as usual.
      </p>
    </div>
  );
}
